import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Video, LayoutDashboard } from 'lucide-react';
import baseapi from '../../services/baseapi';

export interface CameraType {
    id: string;
    name: string;
    status: string;
    url: string;
    client_id?: string;
    agent_id?: string | number | null;
}

interface LiveStreamModalProps {
    open: boolean;
    onClose: () => void;
    camera: CameraType | null;
}

export const VideoPlayer = ({ camera, onStateChange, isSinglePlayer = false }: { camera: CameraType; onStateChange?: (state: 'connecting' | 'live' | 'offline') => void; isSinglePlayer?: boolean }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        let pc: RTCPeerConnection | null = null;
        let hls: any = null;
        let retryTimer: any = null;
        let retries = 0;
        const MAX_RETRIES = 6;

        let streamId = '';
        let readToken = '';
        let hlsUrl = '';
        let isUsingHlsFallback = false;
        let isMounted = true;

        const startHls = (url: string, token: string) => {
            if (!isMounted) return;
            const Hls = (window as any).Hls;
            if (!Hls) {
                setError("HLS library not loaded");
                setIsLoading(false);
                onStateChange?.('offline');
                return;
            }

            if (hls) {
                hls.destroy();
            }

            hls = new Hls({
                debug: false,
                manifestLoadingTimeOut: 65000,
                manifestLoadingMaxRetry: 0,
                xhrSetup: function (xhr: any, url: string) {
                    if (token && !url.includes('token=')) {
                        xhr.open('GET', url + (url.includes('?') ? '&' : '?') + 'token=' + token, true);
                    }
                },
            });

            hls.on(Hls.Events.ERROR, (_: any, d: any) => {
                if (!isMounted) return;
                console.warn(`[hls] ${d.type}/${d.details} fatal=${d.fatal}`);
                if (d.fatal) {
                    hls.destroy();
                    hls = null;
                    if (retries < 8) {
                        retries++;
                        const doRefresh = (retries % 2 === 0);
                        console.log(`[hls] Stream not ready - retrying in 3s... (${retries}/8)`);
                        setIsLoading(true);
                        onStateChange?.('connecting');

                        if (doRefresh) {
                            refreshStream().then((freshData) => {
                                if (!isMounted) return;
                                if (freshData) {
                                    hlsUrl = freshData.hls_url;
                                    readToken = freshData.read_token || freshData.hls_url.split('token=')[1] || '';
                                }
                                retryTimer = setTimeout(() => {
                                    if (isMounted) startHls(hlsUrl, readToken);
                                }, 3000);
                            });
                        } else {
                            retryTimer = setTimeout(() => {
                                if (isMounted) startHls(hlsUrl, readToken);
                            }, 3000);
                        }
                    } else {
                        setError("Camera fallback stream unavailable");
                        setIsLoading(false);
                        onStateChange?.('offline');
                    }
                }
            });

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (!isMounted) return;
                setError(null);
                setIsLoading(false);
                onStateChange?.('live');
                video.play().catch(e => console.warn('autoplay: ' + e));
            });

            hls.loadSource(url);
            hls.attachMedia(video);
        };

        const refreshStream = async () => {
            if (!isMounted) return null;
            try {
                const response = await baseapi.post('camera/stream-access', {
                    asset_id: camera.id,
                    client_id: camera.client_id,
                    agent_id: camera.agent_id,
                });
                if (isMounted && response.data.status) {
                    return response.data.data;
                }
            } catch (err) {
                console.error("Failed to refresh stream", err);
            }
            return null;
        };

        const startHlsFallback = () => {
            if (!isMounted) return;
            console.log("[webrtc] Falling back to HLS stream...");
            setIsLoading(true);
            onStateChange?.('connecting');
            isUsingHlsFallback = true;
            if (pc) {
                pc.close();
                pc = null;
            }
            if (video && video.srcObject) {
                video.srcObject = null;
            }
            if (hlsUrl) {
                const token = readToken || hlsUrl.split('token=')[1] || '';
                startHls(hlsUrl, token);
            } else {
                setError("Camera stream unavailable");
                setIsLoading(false);
                onStateChange?.('offline');
            }
        };

        const handleRetry = () => {
            if (!isMounted || isUsingHlsFallback) return;

            if (retries >= MAX_RETRIES) {
                startHlsFallback();
                return;
            }

            retries++;
            console.log(`[webrtc] Connection failed/disconnected. Retry ${retries}/${MAX_RETRIES} in 4s...`);
            setIsLoading(true);
            onStateChange?.('connecting');

            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = setTimeout(async () => {
                if (!isMounted) return;
                if (retries % 2 === 0) {
                    const freshData = await refreshStream();
                    if (!isMounted) return;
                    if (freshData) {
                        streamId = freshData.stream_id;
                        readToken = freshData.read_token;
                        hlsUrl = freshData.hls_url;
                    }
                }
                connectWebRTC();
            }, 4000);
        };

        const connectWebRTC = async () => {
            if (!isMounted || isUsingHlsFallback) return;

            if (pc) {
                pc.close();
                pc = null;
            }

            video.srcObject = null;

            if (!streamId) {
                startHlsFallback();
                return;
            }

            try {
                pc = new RTCPeerConnection({
                    iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
                });

                pc.addTransceiver('video', { direction: 'recvonly' });
                pc.addTransceiver('audio', { direction: 'recvonly' });

                pc.ontrack = (event) => {
                    if (!isMounted) return;
                    if (!video.srcObject) {
                        video.srcObject = new MediaStream();
                    }
                    (video.srcObject as MediaStream).addTrack(event.track);
                    setError(null);
                };

                pc.oniceconnectionstatechange = () => {
                    if (!isMounted) return;
                    const state = pc?.iceConnectionState;
                    console.log(`[webrtc] ICE Connection State: ${state}`);
                    if (state === 'connected' || state === 'completed') {
                        retries = 0;
                        setError(null);
                        setIsLoading(false);
                        onStateChange?.('live');
                    } else if (state === 'failed' || state === 'disconnected') {
                        handleRetry();
                    }
                };

                const offer = await pc.createOffer();
                if (!isMounted) return;
                await pc.setLocalDescription(offer);

                await new Promise<void>((resolve) => {
                    if (!isMounted || pc?.iceGatheringState === 'complete') {
                        resolve();
                        return;
                    }
                    const checkGathering = () => {
                        if (!isMounted || pc?.iceGatheringState === 'complete') {
                            pc?.removeEventListener('icegatheringstatechange', checkGathering);
                            resolve();
                        }
                    };
                    pc?.addEventListener('icegatheringstatechange', checkGathering);
                    setTimeout(() => {
                        pc?.removeEventListener('icegatheringstatechange', checkGathering);
                        resolve();
                    }, 1000);
                });

                if (!isMounted) return;

                const whepUrl = `https://${window.location.hostname}/stream/${streamId}/whep?token=${readToken}`;
                console.log(`[webrtc] WHEP handshake with: ${whepUrl}`);

                const response = await fetch(whepUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/sdp' },
                    body: pc.localDescription?.sdp
                });

                if (!isMounted) return;

                if (!response.ok) {
                    throw new Error(`WHEP failed with status: ${response.status}`);
                }

                const answerSdp = await response.text();
                if (!isMounted) return;

                await pc.setRemoteDescription(new RTCSessionDescription({
                    type: 'answer',
                    sdp: answerSdp
                }));

                console.log("[webrtc] WebRTC playback started successfully");
            } catch (err) {
                console.warn("[webrtc] Handshake failed, scheduling retry:", err);
                handleRetry();
            }
        };

        const initStream = async () => {
            setIsLoading(true);
            onStateChange?.('connecting');
            const data = await refreshStream();
            if (!isMounted) return;
            if (data) {
                streamId = data.stream_id;
                readToken = data.read_token;
                hlsUrl = data.hls_url;

                if (streamId) {
                    connectWebRTC();
                } else {
                    startHlsFallback();
                }
            } else {
                setError("Failed to get stream access");
                setIsLoading(false);
                onStateChange?.('offline');
            }
        };

        initStream();

        return () => {
            isMounted = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (pc) {
                pc.close();
                pc = null;
            }
            if (hls) {
                hls.destroy();
                hls = null;
            }
        };
    }, [camera]);

    if (error) {
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white p-4 z-30">
                <p className="text-red-500 text-sm mb-2">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-3 py-1 bg-gray-800 rounded text-xs hover:bg-gray-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className={`absolute inset-0 w-full h-full flex items-center justify-center bg-black overflow-hidden ${isSinglePlayer ? 'p-2' : ''}`}>
            {isLoading && !error && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
                    <div className="relative">
                        <div className="w-12 h-12 border-4 border-[#422568]/10 border-t-[#422568] rounded-full animate-spin" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LayoutDashboard className="w-4 h-4 text-[#422568] animate-pulse" />
                        </div>
                    </div>
                </div>
            )}
            <video
                ref={videoRef}
                className="w-full h-full object-contain"
                controls
                autoPlay
                muted
            />
        </div>
    );
};

export const LiveStreamModal: React.FC<LiveStreamModalProps> = ({ open, onClose, camera }) => {
    const [streamStates, setStreamStates] = useState<Record<string, 'connecting' | 'live' | 'offline'>>({});

    if (!open || !camera) return null;

    return createPortal(
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[9999] flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="flex items-center gap-4">
                        <div className="p-2 bg-[#422568]/10 rounded-xl shadow-sm border border-[#422568]/20"><Video className="w-5 h-5 text-[#422568]" /></div>
                        <div>
                            <h3 className="text-xs font-black uppercase tracking-[0.1em] text-slate-900">{camera.name}</h3>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-0.5 flex items-center gap-2">
                                {(() => {
                                    const currentStatus = streamStates[camera.id] || 'connecting';
                                    const dotColor = currentStatus === 'live' ? 'bg-emerald-500' : currentStatus === 'connecting' ? 'bg-amber-500' : 'bg-red-500';
                                    return (
                                        <>
                                            <div className={`w-1.5 h-1.5 rounded-full ${dotColor} animate-pulse`} />
                                            <span>Live Stream Feed ({currentStatus === 'live' ? 'Live' : currentStatus === 'connecting' ? 'Connecting...' : 'Offline'})</span>
                                        </>
                                    );
                                })()}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-all group">
                        <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                    </button>
                </div>
                <div className="p-4 bg-slate-100/30">
                    <div className="relative aspect-video bg-black flex flex-col items-center justify-center rounded-xl overflow-hidden shadow-2xl border border-slate-200 shadow-inner">
                        {camera.status === 'online' ? (
                            <VideoPlayer
                                camera={camera}
                                onStateChange={(state) => setStreamStates(prev => ({ ...prev, [camera.id]: state }))}
                                isSinglePlayer={true}
                            />
                        ) : (
                            <div className="text-center">
                                <Video className="w-12 h-12 text-red-500/80 mx-auto mb-4 animate-pulse" />
                                <p className="text-slate-300 text-sm font-semibold">Camera is offline</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default LiveStreamModal;
